/**
 * Accordion Features — GameBreaker storefront
 *
 * - Expand/collapse in place. Never scroll the page on open/close.
 * - Same-page anchors (#scroll or any #id) smooth-scroll so the target
 *   (its heading, if it has one) sits in the vertical center of the
 *   visible viewport, below sticky/fixed headers.
 */
(function (window, document) {
  'use strict';

  var ROOT = '.accordion-features-wrapper';
  var LISTENING = 'data-accf-listening';
  var BOUND = 'data-accf-bound';

  var HEADER_CANDIDATES = [
    '[data-sticky-header]',
    '[data-header-sticky]',
    '#shopify-section-header',
    '[id^="shopify-section-"][id$="__header"]',
    '.shopify-section-header-sticky',
    '.header-wrapper',
    '.header--sticky',
    '.section-header',
    'sticky-header',
    'header.header',
    'header[role="banner"]',
    'header',
    '#header',
    '.announcement-bar',
    '[data-announcement-bar]',
  ].join(',');

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getStickyTopOffset() {
    var offset = 0;
    var nodes = [];
    try {
      nodes = document.querySelectorAll(HEADER_CANDIDATES);
    } catch (err) {
      nodes = [];
    }

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
        continue;
      }
      var position = style.position;
      if (position !== 'fixed' && position !== 'sticky') continue;
      var rect = el.getBoundingClientRect();
      if (rect.height < 1) continue;
      if (rect.top <= 2 && rect.bottom > 0) {
        offset = Math.max(offset, Math.round(rect.bottom));
      }
    }

    var root = window.getComputedStyle(document.documentElement);
    var cssHeader = parseFloat(root.getPropertyValue('--header-height')) || 0;
    var cssOffset = parseFloat(root.getPropertyValue('--header-offset')) || 0;
    return Math.max(offset, Math.round(cssHeader + cssOffset));
  }

  function clampScrollTop(top) {
    var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    return Math.min(Math.max(0, Math.round(top)), max);
  }

  function resolveScrollBox(target) {
    if (!target) return null;
    if (target.id && target.id.indexOf('custom_section_XjgijD') !== -1) return target;
    if (target.id && /^d3[o0]$/i.test(target.id)) {
      return target.closest('.shopify-section') || target.parentElement || target;
    }
    if (isD3OBox(target)) {
      return (target.closest && target.closest('.shopify-section')) || target;
    }
    return target;
  }

  function isD3OBox(box) {
    if (!box) return false;
    if (box.id && (/^d3[o0]$/i.test(box.id) || box.id.indexOf('custom_section_XjgijD') !== -1)) {
      return true;
    }
    return !!(box.querySelector && box.querySelector('img[src*="D3O-Background"], img[src*="D3O-"]'));
  }

  function scrollElementToCenter(target) {
    if (!target) return;

    var box = resolveScrollBox(target);

    function midpointDelta() {
      var sticky = getStickyTopOffset();
      var available = Math.max(80, window.innerHeight - sticky);
      var rect = box.getBoundingClientRect();

      /*
       * A full-height D3O block (~800px) is as tall as the window. Centering
       * its midpoint (or pinning its top) both glue it under the header.
       * Park the section top ~30% down the visible area so the orange block
       * sits in the middle of the screen with the previous section peeking
       * above — not flush to the header, not starting at the halfway line.
       */
      if (isD3OBox(box)) {
        var desiredTop = sticky + available * 0.3;
        return rect.top - desiredTop;
      }

      var usedHeight = Math.min(rect.height, available * 0.55);
      var viewMid = sticky + available / 2;
      var boxMid = rect.top + usedHeight / 2;
      return boxMid - viewMid;
    }

    window.scrollTo({
      top: clampScrollTop(window.scrollY + midpointDelta()),
      left: 0,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });

    window.setTimeout(function () {
      if (!box.isConnected) return;
      var delta = midpointDelta();
      if (Math.abs(delta) > 16) {
        window.scrollTo({
          top: clampScrollTop(window.scrollY + delta),
          left: 0,
          behavior: 'auto',
        });
      }
    }, 450);
  }

  function decodeHash(hash) {
    if (!hash || hash === '#') return '';
    var raw = hash.charAt(0) === '#' ? hash.slice(1) : hash;
    if (!raw) return '';
    try {
      return decodeURIComponent(raw);
    } catch (err) {
      return raw;
    }
  }

  function findD3OSection() {
    var shopify = document.querySelector('[id*="__custom_section_XjgijD"]');
    if (shopify) return shopify;

    var img = document.querySelector(
      'img[src*="D3O-Background"], img[src*="D3O-"], img[src*="/D3O"], img[src*="d3o"]'
    );
    if (img) return img.closest('.shopify-section') || img;

    var byId =
      document.getElementById('D3O') ||
      document.getElementById('d3o') ||
      document.getElementById('D30') ||
      document.getElementById('d30');
    if (byId) return byId.closest ? byId.closest('.shopify-section') || byId : byId;

    return null;
  }

  function ensureD3OAnchor() {
    return findD3OSection();
  }

  function isD3OItem(item) {
    if (!item) return false;
    var title = item.querySelector('.accf-item-title');
    var text = title ? title.textContent : '';
    return /d3[o0]/i.test(text);
  }

  function resolveHashTarget(hash) {
    var id = decodeHash(hash);
    if (!id) return null;
    if (/^d3[o0]$/i.test(id)) return ensureD3OAnchor();
    var byId = document.getElementById(id);
    if (byId) return byId;
    try {
      var escaped = window.CSS && CSS.escape ? CSS.escape(id) : id.replace(/"/g, '\\"');
      return document.querySelector('[name="' + escaped + '"]');
    } catch (err) {
      return null;
    }
  }

  function samePageHash(link) {
    if (!link) return '';
    var href = link.getAttribute('href');
    if (!href) return '';
    try {
      var url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return '';
      var here = window.location.pathname.replace(/\/$/, '');
      var there = url.pathname.replace(/\/$/, '');
      if (here !== there) return '';
      return url.hash || '';
    } catch (err) {
      return href.charAt(0) === '#' ? href : '';
    }
  }

  function setItemOpen(item, open) {
    var btn = item.querySelector('.accf-item-header');
    var panel = item.querySelector('.accf-item-panel');
    item.classList.toggle('is-open', open);
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.setAttribute('aria-hidden', open ? 'false' : 'true');
  }

  function enhanceItem(item, index) {
    var btn = item.querySelector('.accf-item-header');
    var panel = item.querySelector('.accf-item-panel');
    if (!btn) return;
    btn.setAttribute(BOUND, 'true');
    btn.setAttribute('type', 'button');
    if (panel && !panel.id) {
      panel.id = 'accf-panel-' + index + '-' + String(item.getAttribute('data-accf-index') || 'x');
      btn.setAttribute('aria-controls', panel.id);
    }
    var initiallyOpen =
      item.classList.contains('is-open') || btn.getAttribute('aria-expanded') === 'true';
    setItemOpen(item, initiallyOpen);

    if (isD3OItem(item)) {
      var d3oLinks = item.querySelectorAll('.accf-item-panel-inner a[href]');
      for (var l = 0; l < d3oLinks.length; l++) {
        d3oLinks[l].setAttribute('href', '#D3O');
        d3oLinks[l].setAttribute('title', '#D3O');
      }
    }
  }

  function enhanceAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var wrappers = [];
    if (root.classList && root.classList.contains('accordion-features-wrapper')) {
      wrappers.push(root);
    }
    if (root.querySelectorAll) {
      var found = root.querySelectorAll(ROOT);
      for (var i = 0; i < found.length; i++) wrappers.push(found[i]);
    }
    for (var w = 0; w < wrappers.length; w++) {
      var items = wrappers[w].querySelectorAll('.accf-item');
      for (var j = 0; j < items.length; j++) enhanceItem(items[j], j);
    }
  }

  function pinScroll(ms) {
    var x = window.scrollX;
    var y = window.scrollY;
    var end = (window.performance && performance.now ? performance.now() : Date.now()) + (ms || 320);

    function tick() {
      if (window.scrollX !== x || window.scrollY !== y) {
        window.scrollTo(x, y);
      }
      var now = window.performance && performance.now ? performance.now() : Date.now();
      if (now < end) {
        window.requestAnimationFrame(tick);
      }
    }

    if (window.scrollX !== x || window.scrollY !== y) {
      window.scrollTo(x, y);
    }
    window.requestAnimationFrame(tick);
  }

  function toggleFromHeader(btn) {
    var item = btn.closest('.accf-item');
    if (!item) return;
    var willOpen = !item.classList.contains('is-open');
    setItemOpen(item, willOpen);
    try {
      btn.focus({ preventScroll: true });
    } catch (err) {
      /* ignore */
    }
    pinScroll(320);
  }

  function stop(event) {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
  }

  function onDocumentClick(event) {
    if (event.button != null && event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var target = event.target;
    if (!target || !target.closest) return;

    var header = target.closest('.accf-item-header');
    if (header && header.closest(ROOT)) {
      stop(event);
      toggleFromHeader(header);
      return;
    }

    var link = target.closest('a[href]');
    if (!link || link.target === '_blank') return;

    var inAccordion = !!link.closest('.scrollacc, ' + ROOT);
    var item = link.closest('.accf-item');
    if (inAccordion && isD3OItem(item)) {
      stop(event);
      var d3o = ensureD3OAnchor();
      if (d3o) {
        window.requestAnimationFrame(function () {
          scrollElementToCenter(d3o);
        });
      }
      return;
    }

    var hash = samePageHash(link);
    if (!hash) return;
    if (!inAccordion && hash !== '#scroll' && !/^#d3[o0]$/i.test(hash)) return;

    var destination = resolveHashTarget(hash);
    if (!destination) return;

    stop(event);

    window.requestAnimationFrame(function () {
      scrollElementToCenter(destination);
    });
  }

  function start() {
    if (document.documentElement.getAttribute(LISTENING) === 'true') {
      enhanceAll(document);
      return;
    }
    document.documentElement.setAttribute(LISTENING, 'true');
    document.addEventListener('click', onDocumentClick, true);
    ensureD3OAnchor();
    enhanceAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    enhanceAll(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.AccordionFeatures = {
    init: enhanceAll,
    scrollElementToCenter: scrollElementToCenter,
  };
})(window, document);
